import { useState, useCallback } from "react"
import { useDispatch } from "react-redux"
import { Button, notification } from "antd"
import { buildGuestTypes, sumCheckedIn, sumCheckedOut } from "./checkInUtils"

// Shared partial check-in / check-out flow for private bookings. Owns the optimistic per-type
// checked-in AND checked-out counts, the modal targets (check-in / check-out / incomplete-docs),
// and the "N checked-in|out / Undo" toast.
//
// `checkInThunk(id, guestTypes, { override })` and `checkOutThunk(id, guestTypes)` receive the
// new absolute per-type counts (full type objects) and must return a dispatchable promise.
// `onSuccess` (optional) runs after any successful mutation/undo — e.g. to refetch a slice the
// reducer doesn't touch (the Action Queue / upcoming detail).
export const useCheckInFlow = (checkInThunk, checkOutThunk, onSuccess) => {
  const dispatch = useDispatch()
  // id -> { [typeKey]: { in: checkedInCount, out: checkedOutCount } }
  const [overrides, setOverrides] = useState({})
  const [checkInItem, setCheckInItem] = useState(null)
  const [checkOutItem, setCheckOutItem] = useState(null)
  const [incompleteItem, setIncompleteItem] = useState(null)
  const [isOverriding, setIsOverriding] = useState(false)

  // Per-type breakdown for a row, override-aware (falls back to a single synthetic type).
  const getGuestTypes = useCallback(
    (item) => {
      const base = item.guestTypes?.length
        ? item.guestTypes
        : buildGuestTypes(null, item.guestCount ?? item.totalGuests ?? 0, item.checkedInCount ?? 0, item.checkedOutCount ?? 0)
      const override = overrides[item.id]
      if (!override) return base
      return base.map((type) => {
        const ov = override[type.key]
        if (!ov) return type
        return { ...type, checkedInCount: ov.in ?? type.checkedInCount, checkedOutCount: ov.out ?? type.checkedOutCount }
      })
    },
    [overrides]
  )

  const getCheckedInCount = useCallback((item) => sumCheckedIn(getGuestTypes(item)), [getGuestTypes])
  const getCheckedOutCount = useCallback((item) => sumCheckedOut(getGuestTypes(item)), [getGuestTypes])

  const setOverride = (id, guestTypes) =>
    setOverrides((prev) => ({
      ...prev,
      [id]: Object.fromEntries(guestTypes.map((type) => [type.key, { in: type.checkedInCount ?? 0, out: type.checkedOutCount ?? 0 }]))
    }))

  // kind: "in" | "out". `newTypes`/`previousTypes` are full per-type objects.
  const commit = useCallback(
    (kind, item, newTypes, previousTypes, override) => {
      setOverride(item.id, newTypes)
      const thunk = kind === "in" ? checkInThunk : checkOutThunk
      const sum = kind === "in" ? sumCheckedIn : sumCheckedOut
      const undo = (key) => {
        setOverride(item.id, previousTypes)
        dispatch(thunk(item.id, previousTypes, { override: true })).then(() => onSuccess?.())
        notification.destroy(key)
      }
      dispatch(thunk(item.id, newTypes, { override }))
        .then(() => {
          onSuccess?.()
          const added = sum(newTypes) - sum(previousTypes)
          if (added <= 0) return
          const key = `check-${kind}-${item.id}-${Date.now()}`
          notification.open({
            key,
            message: `${added} guest${added === 1 ? "" : "s"} checked-${kind === "in" ? "in" : "out"}`,
            placement: "bottomRight",
            btn: (
              <Button type="link" size="small" onClick={() => undo(key)}>
                Undo
              </Button>
            )
          })
        })
        .catch(() => setOverride(item.id, previousTypes))
    },
    [dispatch, checkInThunk, checkOutThunk, onSuccess]
  )

  // Open the check-in modal. Gated by the required-documents check (Incomplete Documents).
  // `item` must carry `guestTypes` (use getGuestTypes) so the modal shows the current counts.
  const openCheckIn = useCallback((item, waiversComplete) => {
    setIsOverriding(false)
    if (waiversComplete) setCheckInItem(item)
    else setIncompleteItem(item)
  }, [])

  // Open the check-out modal (no documents gate — they're already checked in).
  const openCheckOut = useCallback((item) => setCheckOutItem(item), [])

  // Reset a fully-checked-out booking back to pending (clears both counts).
  const reset = useCallback(
    (item) => {
      const previousTypes = getGuestTypes(item)
      const zeroed = previousTypes.map((type) => ({ ...type, checkedInCount: 0, checkedOutCount: 0 }))
      setOverride(item.id, zeroed)
      dispatch(checkInThunk(item.id, zeroed, { override: true })).then(() => onSuccess?.())
    },
    [getGuestTypes, dispatch, checkInThunk, onSuccess]
  )

  const handleOverride = useCallback(() => {
    setIsOverriding(true)
    setCheckInItem(incompleteItem)
    setIncompleteItem(null)
  }, [incompleteItem])

  const handleConfirmCheckIn = useCallback(
    (newTypes) => {
      if (!checkInItem) return
      const previousTypes = checkInItem.guestTypes ?? []
      setCheckInItem(null)
      commit("in", checkInItem, newTypes, previousTypes, isOverriding)
    },
    [checkInItem, isOverriding, commit]
  )

  const handleConfirmCheckOut = useCallback(
    (newTypes) => {
      if (!checkOutItem) return
      const previousTypes = checkOutItem.guestTypes ?? []
      setCheckOutItem(null)
      commit("out", checkOutItem, newTypes, previousTypes, false)
    },
    [checkOutItem, commit]
  )

  const closeCheckIn = useCallback(() => setCheckInItem(null), [])
  const closeCheckOut = useCallback(() => setCheckOutItem(null), [])
  const closeIncomplete = useCallback(() => setIncompleteItem(null), [])

  return {
    getGuestTypes,
    getCheckedInCount,
    getCheckedOutCount,
    openCheckIn,
    openCheckOut,
    reset,
    handleOverride,
    handleConfirmCheckIn,
    handleConfirmCheckOut,
    checkInItem,
    checkOutItem,
    incompleteItem,
    closeCheckIn,
    closeCheckOut,
    closeIncomplete
  }
}
